import React, { useState } from 'react';
import { Box, IconButton, Modal, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    Color,
    ConfirmWarningModal,
    ObjectGroupForm,
    ObjectGroupFormData,
    ObjectGroupType,
} from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { useTiersService } from '@europrocurement/l2d-domain';
import { AnyAction } from '@reduxjs/toolkit';
import { useSocieteGroupAction, useSocieteGroupActionDispatch } from '../../../providers';
import { useSocietesObjectGroup } from '../../../hooks';

export const ModalSocieteGroupAction = function () {
    const { action, selectedSocieteGroup, modalOpen } = useSocieteGroupAction();
    const dispatch = useDispatch();
    const dispatchModal = useSocieteGroupActionDispatch();
    const [submitingProcess, setSubmitingProcess] = useState<boolean>(false);
    const { getObjectGroup } = useTiersService();
    const { palette } = useTheme();
    const { editTiersObjectGroup, deleteTiersObjectGroup } = useSocietesObjectGroup();

    const handleCloseModal = () => {
        setSubmitingProcess(false);
        dispatchModal({ type: 'close_and_reset' });
    };

    const handleEdit = async (group: ObjectGroupFormData) => {
        if (selectedSocieteGroup) {
            const groupPayload = {
                label: group.namegroup,
                color: group.color,
                id: selectedSocieteGroup.id,
            };

            try {
                await editTiersObjectGroup(groupPayload as ObjectGroupType);
                enqueueSnackbar('Votre groupe a bien été édité');
                dispatch(getObjectGroup({}) as unknown as AnyAction);
                handleCloseModal();
            } catch (err) {
                console.error(err);
                enqueueSnackbar("Une erreur est survenue durant l'édition de votre groupe", {
                    variant: 'error',
                });
            }
        } else {
            enqueueSnackbar(
                "Une erreur est survenue durant l'édition de votre groupe, groupe non trouvé",
                {
                    variant: 'error',
                },
            );
        }
    };

    const handleDelete = async () => {
        if (selectedSocieteGroup) {
            const groupPayload = {
                id: selectedSocieteGroup.id,
            };
            try {
                await deleteTiersObjectGroup(groupPayload as ObjectGroupType);
                enqueueSnackbar('Votre groupe a bien été supprimé');
                dispatch(getObjectGroup({}) as unknown as AnyAction);
                handleCloseModal();
            } catch (err) {
                console.error(err);
                enqueueSnackbar('Une erreur est survenue durant la suppression de votre groupe', {
                    variant: 'error',
                });
            }
        } else {
            enqueueSnackbar(
                'Une erreur est survenue durant la suppression de votre groupe, groupe non trouvé',
                {
                    variant: 'error',
                },
            );
        }
    };

    const societeGroupeColors: Color[] = [
        'lightGreen',
        'red',
        'pink',
        'purple',
        'deepPurple',
        'amber',
        'blueGrey',
        'teal',
        'deepOrange',
        'brown',
        'cyan',
        'green',
        'indigo',
        'lightBlue',
        'lime',
        'orange',
        'yellow',
        'blue',
    ];

    if (action === 'edit') {
        return (
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="create-modal-title"
                aria-describedby="create-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{
                            fontSize: '16px',
                            position: 'absolute',
                            right: '20px',
                            top: '35px',
                            color: palette.grey[500],
                        }}
                    >
                        <FaOptionIcon icon={['fas', 'close']} />
                    </IconButton>

                    <ObjectGroupForm
                        title="Editer un groupe de société"
                        fieldLabel="Nom"
                        submitButtonLabel="Modifier"
                        onSubmit={handleEdit}
                        onCancel={handleCloseModal}
                        colors={societeGroupeColors}
                        nameValue={selectedSocieteGroup?.label}
                        colorValue={selectedSocieteGroup?.color as Color}
                    />
                </Box>
            </Modal>
        );
    }
    if (action === 'delete') {
        return (
            <ConfirmWarningModal
                actionOnValidation={handleDelete}
                handleCloseModal={handleCloseModal}
                modalOpen={modalOpen}
                submitingProcess={submitingProcess}
                title={`Supprimer ${selectedSocieteGroup?.label} ?`}
                message="Êtes-vous sûr de vouloir supprimer ce groupe de société ? Cette action est définitive"
                validationButtonText="Oui, supprimer"
            />
        );
    }
    return null;
};
